import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import {
  Box,
  Button,
  Container,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { Flashcard } from '.';
import { Stack } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

type PropTypes = {
  flashcards?: Flashcard[];
};

export const FlashcardMode = ({ flashcards }: PropTypes) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [shuffledFlashcards, setShuffledFlashcards] = useState<Flashcard[]>();
  const [currentFlashcardPosition, setCurrentFlashcardPosition] = useState(0);
  const [currentFlashcard, setCurrentFlashcard] = useState<Flashcard>();
  const [showAnswer, setShowAnswer] = useState(false);
  const [showFirstLetter, setShowFirstLetter] = useState(false);
  const [showLetterBlanks, setShowLetterBlanks] = useState(false);
  const [pressedKey, setPressedKey] = useState<string>();
  const [hideCategoryAndPar, setHideCategoryAndPar] = useState(false);

  useEffect(() => {
    const keyDownHandler = (event: any) => {
      // event.preventDefault();
      setPressedKey((prev) => event.key);
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  useEffect(() => {
    if (!flashcards) return;
    const shuffledFlashcards = shuffleFisherYates(structuredClone(flashcards));
    setShuffledFlashcards(shuffledFlashcards);
    setCurrentFlashcard(shuffledFlashcards[0]);
  }, [flashcards]);

  useEffect(() => {
    switch (pressedKey?.toUpperCase()) {
      case 'A':
        handleShowFirstLetterClick();
        break;
      case 'S':
        handleShowLetterBlanksClick();
        break;
      case 'D':
        handleShowAnswerClick();
        break;
      case 'F':
        handleNextClick();
        break;
      case 'ENTER':
        handleNextClick();
        break;
      case 'ARROWLEFT':
        handlePrevClick();
        break;
      case 'ARROWRIGHT':
        handleNextClick();
        break;

      default:
        break;
    }
    setPressedKey(undefined);
  }, [pressedKey]);

  useEffect(() => {
    if (!shuffledFlashcards) return;
    if (currentFlashcardPosition >= 0) {
      setCurrentFlashcard(shuffledFlashcards[currentFlashcardPosition]);
    }
  }, [currentFlashcardPosition]);

  const handlePrevClick = () => {
    if (currentFlashcardPosition === 0) return;
    setCurrentFlashcardPosition((prev) => prev - 1);
    setShowAnswer(false);
    setShowFirstLetter(false);
    setShowLetterBlanks(false);
  };

  const handleNextClick = () => {
    if (!shuffledFlashcards) return;
    if (currentFlashcardPosition === shuffledFlashcards.length - 1) return;
    setCurrentFlashcardPosition((prev) => prev + 1);
    setShowAnswer(false);
    setShowFirstLetter(false);
    setShowLetterBlanks(false);
  };

  const handleShowAnswerClick = () => {
    setShowAnswer(true);
    setShowFirstLetter(false);
    setShowLetterBlanks(false);
  };

  const handleShowFirstLetterClick = () => {
    setShowAnswer(false);
    setShowFirstLetter(true);
    setShowLetterBlanks(false);
  };

  const handleShowLetterBlanksClick = () => {
    setShowAnswer(false);
    setShowFirstLetter(false);
    setShowLetterBlanks(true);
  };

  const handleMarkAsAnsweredClick = () => {};

  return (
    <Container
      sx={{
        padding: '60px',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Button
        variant="outlined"
        sx={{ position: 'absolute', top: 20, left: 20 }}
        onClick={() => navigate('/flashcards')}
      >
        Back to flashcard menu
      </Button>
      <Stack spacing={4}>
        <Stack direction={'row'}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={hideCategoryAndPar}
                  onChange={() => setHideCategoryAndPar((prev) => !prev)}
                />
              }
              label="Hide Category & PAR"
              labelPlacement="start"
            />
          </FormGroup>
        </Stack>
        <Typography textAlign={'center'}>
          {currentFlashcardPosition + 1} of {shuffledFlashcards?.length}
        </Typography>
        <Box
          sx={{
            position: 'relative',
            width: isMobile ? 500 : 800,
            height: isMobile ? 800 : 500,
            backgroundColor: theme.palette.primary.dark,
            borderRadius: '1rem',
          }}
        >
          <Stack
            spacing={1}
            sx={{
              width: '100%',
              position: 'absolute',
              top: '5%',
              display: 'flex',
              justifyContent: 'center',
              visibility: hideCategoryAndPar ? 'hidden' : 'visible',
            }}
          >
            <Typography
              variant="h6"
              textAlign={'center'}
              fontWeight={600}
              sx={{ color: theme.palette.primary.contrastText }}
            >
              {currentFlashcard?.category}
            </Typography>
            <Typography
              variant="h6"
              textAlign={'center'}
              fontWeight={600}
              sx={{ color: theme.palette.primary.contrastText }}
            >
              PAR {currentFlashcard?.par}
            </Typography>
          </Stack>
          <Stack
            spacing={5}
            sx={{
              width: '100%',
              position: 'absolute',
              top: '35%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h5" textAlign={'center'}>
              {currentFlashcard?.question}
            </Typography>
          </Stack>
          <Stack
            spacing={5}
            sx={{
              width: '100%',
              position: 'absolute',
              top: '65%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {showAnswer && currentFlashcard ? (
              <Stack
                direction="row"
                spacing={4}
                justifyContent="center"
                sx={{ flexWrap: 'wrap' }}
              >
                {currentFlashcard.answer.split(' ').map((word) => (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    justifyContent="center"
                    sx={{ flexWrap: 'wrap' }}
                  >
                    {word.split('').map((letter) => (
                      <Paper
                        elevation={6}
                        variant={'elevation'}
                        sx={{
                          alignContent: 'center',
                          width: '40px',
                          height: '40px',
                          border: '1px solid black',
                        }}
                      >
                        <Typography textAlign="center" variant="h4">
                          {letter.toUpperCase()}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                ))}
              </Stack>
            ) : null}
            {showFirstLetter ? (
              <Stack direction="row" spacing={0.5} justifyContent="center">
                <Paper
                  elevation={6}
                  variant={'elevation'}
                  sx={{
                    alignContent: 'center',
                    width: '40px',
                    height: '40px',
                    border: '1px solid black',
                  }}
                >
                  <Typography textAlign="center" variant="h4">
                    {currentFlashcard?.answer.charAt(0).toUpperCase()}
                  </Typography>
                </Paper>
              </Stack>
            ) : null}
            {showLetterBlanks && currentFlashcard ? (
              <Stack direction="row" spacing={4} justifyContent="center">
                {create2dRevealedLettersArray(
                  currentFlashcard.answer.split('')
                ).map((word) => (
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    {word.map((letter) => (
                      <Paper
                        elevation={6}
                        variant={'elevation'}
                        sx={{
                          alignContent: 'center',
                          width: '40px',
                          height: '40px',
                          border: '1px solid black',
                        }}
                      >
                        <Typography textAlign="center" variant="h4">
                          {' '}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={5}
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button variant="text" onClick={handleShowFirstLetterClick}>
            First Letter (A)
          </Button>
          <Button variant="text" onClick={handleShowLetterBlanksClick}>
            Blanks (S)
          </Button>
          <Button variant="text" onClick={handleShowAnswerClick}>
            Answer (D)
          </Button>
          <Button variant="text" onClick={handleMarkAsAnsweredClick}>
            Complete (F or Enter)
          </Button>
        </Stack>
        <Stack
          direction="row"
          spacing={10}
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button variant="outlined" onClick={handlePrevClick}>
            PREV ({`<-`})
          </Button>
          <Button variant="outlined" onClick={handleNextClick}>
            NEXT ({`->`})
          </Button>
        </Stack>
        <Typography textAlign="center" variant="subtitle1">
          Hotkeys in ()
        </Typography>
      </Stack>
    </Container>
  );
};

const create2dRevealedLettersArray = (answerArray: string[]) => {
  const revealedLetters = [];
  let word: string[] = [];
  answerArray.forEach((letter) => {
    // if non-alphanumeric
    if (
      /[\W_]/.test(letter) &&
      Array.from(letter.normalize('NFD')).length === 1
    ) {
      if (letter === ' ') {
        revealedLetters.push(word);
        word = [];
      } else {
        word.push(letter);
      }
    } else {
      word.push('');
    }
  });
  revealedLetters.push(word);
  return revealedLetters;
};

function shuffleFisherYates(array: any[]) {
  let i = array.length;
  while (i--) {
    const ri = Math.floor(Math.random() * i);
    [array[i], array[ri]] = [array[ri], array[i]];
  }
  return array;
}
